import { useEffect, useMemo, useRef, useState } from "react";
import { DateTime } from "luxon";
import { Check, ChevronsUpDown, Clock, Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { appTimeZone } from "@/lib/timezone";
import { fetchTenants } from "@/lib/api/tenants";
import { fetchDoctors } from "@/lib/api/users";
import { fetchCalendars } from "@/lib/api/calendar";
import { calendarOptionLabel } from "@/lib/calendars";
import {
  createAdminSlot,
  fetchAdminSlot,
  updateAdminSlot,
} from "@/lib/api/admin-slots";
import { publishCalendarRefresh } from "@/lib/realtime/appointment-realtime-bridge";
import {
  ComboboxField,
  FieldLabel,
  TimePicker,
  combineDateAndTimeInZone,
  dateToYmd,
  pad2,
  toDoctorOption,
  toTenantOption,
  ymdToDate,
} from "./form-fields";

const WEEKDAY_LABELS = [
  { iso: 1, short: "Mo" },
  { iso: 2, short: "Tu" },
  { iso: 3, short: "We" },
  { iso: 4, short: "Th" },
  { iso: 5, short: "Fr" },
  { iso: 6, short: "Sa" },
  { iso: 7, short: "Su" },
];

// Week-of-month pills for the monthly "Nth weekday" variant. -1 is the sentinel
// for "last occurrence of that weekday in the month".
const MONTH_WEEK_LABELS = [
  { value: 1, short: "1st" },
  { value: 2, short: "2nd" },
  { value: 3, short: "3rd" },
  { value: 4, short: "4th" },
  { value: -1, short: "Last" },
];

// 1..31 with a trailing "Last day of month" sentinel (-1).
const MONTH_DAY_VALUES = Array.from({ length: 31 }, (_, i) => i + 1);

const COMMON_TIMEZONES = [
  "UTC",
  "Europe/Warsaw",
  "Europe/London",
  "Europe/Berlin",
  "Europe/Paris",
  "America/New_York",
  "America/Los_Angeles",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
];

// Timezone to prefill for a NEW record. Honors the app-wide global timezone override when set (so
// newly-created records match what the rest of the app shows), else the machine zone as before.
function browserTimezone() {
  return appTimeZone();
}

function selectedDateToYmd(selectedDate) {
  if (!selectedDate) return dateToYmd(new Date());
  if (typeof selectedDate.toISODate === "function") return selectedDate.toISODate();
  if (selectedDate instanceof Date) return dateToYmd(selectedDate);
  return dateToYmd(new Date());
}

function nextHalfHourYmdHhmm(now = DateTime.now()) {
  const minutesUp = now.minute >= 30 ? 60 - now.minute : 30 - now.minute;
  const next = now.plus({ minutes: minutesUp }).set({ second: 0, millisecond: 0 });
  return { ymd: next.toISODate(), hhmm: next.toFormat("HH:mm") };
}

// Allowed subType values exchanged with the API. "" means "any / unspecified".
const SUB_TYPE_OPTIONS = [
  { value: "", label: "Any" },
  { value: "firstVisit", label: "First visit" },
  { value: "followUp", label: "Follow-up" },
];

// Accept legacy snake_case / kebab-case spellings when hydrating from a saved
// slot — server is forgiving on input, we mirror that here.
function normalizeSubType(raw) {
  const v = String(raw ?? "").trim().toLowerCase();
  if (v === "firstvisit" || v === "first_visit" || v === "first-visit") return "firstVisit";
  if (v === "followup" || v === "follow_up" || v === "follow-up") return "followUp";
  return "";
}

const DEFAULT_FORM = {
  doctorId: "",
  tenantId: "",
  calendarId: "",
  slotType: "static",
  subType: "",
  // Follow-up-only flag stored as metadata._moreTests. Kept in form state as a
  // plain boolean; the value is only persisted into metadata when subType is
  // "followUp" (changing the subtype away from followUp implicitly clears it).
  moreTests: false,
  date: "",
  startTime: "09:00",
  endTime: "09:30",
  timezone: "UTC",
  // Recurrence frequency: "weekly" | "monthly" | "dates". Monthly has two
  // sub-variants ("dayOfMonth" → monthDays; "nthWeekday" → monthWeeks +
  // weekdays). "dates" is an explicit list of YYYY-MM-DD strings.
  frequency: "weekly",
  weekdays: [],
  repeatIntervalWeeks: 1,
  repeatIntervalMonths: 1,
  monthlyVariant: "dayOfMonth",
  monthDays: [],
  monthWeeks: [],
  dates: [],
  excludeDates: [],
  bookingMode: "auto",
  placeholder: "",
  colorHex: "",
};

function deriveInitialForm({ mode, slot, selectedDate, defaultDoctorId, defaultTenantId, canonicalDoc }) {
  const baseTimezone = browserTimezone();
  const ymd = selectedDateToYmd(selectedDate);
  const todayYmd = dateToYmd(new Date());
  const useNextHalfHour = ymd === todayYmd;

  // "duplicate" mirrors "edit" for hydration — the form starts as a clone of
  // the source slot so the user can tweak fields before saving as a new
  // record. The split between edit and duplicate happens at submit time
  // (PUT vs POST), not here.
  if ((mode === "edit" || mode === "duplicate") && (canonicalDoc || slot)) {
    const source = canonicalDoc || slot;
    const tz =
      String(source?.time?.timezone || source?.timezone || baseTimezone).trim() || "UTC";
    const startStr = source?.time?.start || (slot?.startAt?.toISO ? slot.startAt.toISO() : "");
    const endStr = source?.time?.end || (slot?.endAt?.toISO ? slot.endAt.toISO() : "");
    const startDt = DateTime.fromISO(String(startStr || ""), { zone: tz });
    const endDt = DateTime.fromISO(String(endStr || ""), { zone: tz });
    const recurrence = source?.recurrence || null;
    const slotType =
      String(source?.slotType || "static") === "recurring" ? "recurring" : "static";

    // Recurrence may carry its own HH:MM clock independent of the anchor instant.
    const recurrenceStartTime =
      typeof recurrence?.startTime === "string" && /^\d{2}:\d{2}$/.test(recurrence.startTime)
        ? recurrence.startTime
        : null;
    const recurrenceEndTime =
      typeof recurrence?.endTime === "string" && /^\d{2}:\d{2}$/.test(recurrence.endTime)
        ? recurrence.endTime
        : null;

    const rawFrequency = String(recurrence?.frequency || "weekly").toLowerCase();
    const frequency =
      rawFrequency === "monthly" || rawFrequency === "dates" ? rawFrequency : "weekly";
    const monthDays = Array.isArray(recurrence?.monthDays)
      ? recurrence.monthDays.filter((n) => Number.isInteger(n) && (n === -1 || (n >= 1 && n <= 31)))
      : [];
    const monthWeeks = Array.isArray(recurrence?.monthWeeks)
      ? recurrence.monthWeeks.filter((n) => Number.isInteger(n) && (n === -1 || (n >= 1 && n <= 4)))
      : [];
    // Choose the active monthly variant from whichever array the saved slot
    // populated; default to dayOfMonth so the UI defaults match the simpler case.
    const monthlyVariant = monthDays.length > 0 ? "dayOfMonth" : monthWeeks.length > 0 ? "nthWeekday" : "dayOfMonth";

    const dates = Array.isArray(recurrence?.dates)
      ? Array.from(
          new Set(
            recurrence.dates
              .map((d) => (typeof d === "string" ? d.trim() : ""))
              .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d)),
          ),
        ).sort()
      : [];

    const excludeDates = Array.isArray(recurrence?.excludeDates)
      ? Array.from(
          new Set(
            recurrence.excludeDates
              .map((d) => (typeof d === "string" ? d.trim() : ""))
              .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d)),
          ),
        ).sort()
      : [];

    return {
      doctorId: String(source?.doctorId || ""),
      tenantId: String(source?.tenantId || ""),
      calendarId: String(source?.calendarId || ""),
      slotType,
      subType: normalizeSubType(source?.subType ?? slot?.subType),
      moreTests: source?.metadata?._moreTests === true || slot?.metadata?._moreTests === true,
      date: startDt.isValid ? startDt.toISODate() : ymd,
      startTime: recurrenceStartTime || (startDt.isValid ? startDt.toFormat("HH:mm") : "09:00"),
      endTime: recurrenceEndTime || (endDt.isValid ? endDt.toFormat("HH:mm") : "09:30"),
      timezone: tz,
      frequency,
      weekdays: Array.isArray(recurrence?.weekdays)
        ? recurrence.weekdays.filter((n) => Number.isInteger(n) && n >= 1 && n <= 7)
        : [],
      repeatIntervalWeeks: Number(recurrence?.repeatIntervalWeeks) || 1,
      repeatIntervalMonths: Number(recurrence?.repeatIntervalMonths) || 1,
      monthlyVariant,
      monthDays,
      monthWeeks,
      dates,
      excludeDates,
      bookingMode:
        String(source?.bookingMode || "auto") === "manual" ? "manual" : "auto",
      placeholder: String(source?.placeholder || source?.title || slot?.placeholder || slot?.title || "").trim(),
      colorHex: String(source?.metadata?.colorHex || slot?.colorHex || "").trim(),
    };
  }

  let startTime = "09:00";
  let endTime = "09:30";
  if (useNextHalfHour) {
    const next = nextHalfHourYmdHhmm();
    startTime = next.hhmm;
    const endDt = DateTime.fromISO(`${next.ymd}T${next.hhmm}`).plus({ minutes: 30 });
    endTime = endDt.toFormat("HH:mm");
  }

  return {
    ...DEFAULT_FORM,
    date: ymd,
    startTime,
    endTime,
    timezone: baseTimezone,
    doctorId: defaultDoctorId || "",
    tenantId: defaultTenantId || "",
  };
}

function PillButton({ active, onClick, children, type = "button", title }) {
  return (
    <button
      type={type}
      onClick={onClick}
      title={title}
      className={cn(
        "inline-flex h-8 min-w-[36px] items-center justify-center rounded-md border px-3 text-xs font-semibold transition-colors",
        active
          ? "border-primary bg-primary text-primary-foreground shadow-sm"
          : "border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

/**
 * Togglable card for the follow-up "More tests needed" flag. Shows the resulting
 * heatmap chip preview inline so the user sees exactly what they're enabling
 * — clicking anywhere on the card flips the state. Designed as a single
 * `role="switch"` button so screen readers announce the on/off transition.
 */
function MoreTestsToggle({ checked, onToggle }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onToggle(!checked)}
      className={cn(
        "group mt-1 flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-all",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        checked
          ? "border-violet-500/60 bg-violet-500/[0.06] shadow-sm"
          : "border-border bg-background hover:border-foreground/20 hover:bg-muted/40",
      )}
    >
      <div
        className={cn(
          "mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-md border-[1.5px] transition-colors",
          checked
            ? "border-violet-500 bg-violet-500 text-white"
            : "border-border bg-background group-hover:border-foreground/40",
        )}
        aria-hidden="true"
      >
        {checked ? <Check className="size-3.5" strokeWidth={3} /> : null}
      </div>
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-foreground">More tests needed</span>
          <span
            className={cn(
              "rounded-sm px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide transition-colors",
              checked
                ? "bg-violet-500/15 text-violet-700 dark:text-violet-300"
                : "bg-muted text-muted-foreground/70",
            )}
          >
            Follow-up{checked ? "+" : ""}
          </span>
        </div>
        <p className="text-xs leading-relaxed text-muted-foreground">
          Tags this follow-up as needing additional tests. The slot will read{" "}
          <strong className="font-semibold text-foreground">
            Follow-up{checked ? "+" : ""}
          </strong>{" "}
          on the heatmap and downstream surfaces.
        </p>
      </div>
    </button>
  );
}

/**
 * Multi-date picker for the "Specific dates" recurrence variant. Reuses the
 * shared `DatePicker` to pick one date at a time; selecting a date appends it
 * to the list (deduped + sorted in the parent). Each selected date renders as
 * a removable chip.
 */
function DateListPicker({ dates, onAdd, onRemove }) {
  const [draft, setDraft] = useState(null);

  const handleAdd = () => {
    if (!(draft instanceof Date) || Number.isNaN(draft.getTime())) return;
    onAdd(draft);
    setDraft(null);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <DatePicker date={draft} setDate={setDraft} />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9 shrink-0 [&_svg]:size-3.5"
          onClick={handleAdd}
          disabled={!(draft instanceof Date)}
        >
          <Plus />
          Add date
        </Button>
      </div>
      {dates.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No dates yet. Pick a date above and press <em>Add date</em>.
        </p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {dates.map((ymd) => (
            <span
              key={ymd}
              className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/40 px-2 py-1 text-xs font-mono tabular-nums"
            >
              {ymd}
              <button
                type="button"
                onClick={() => onRemove(ymd)}
                className="inline-flex size-4 items-center justify-center rounded text-muted-foreground/70 transition-colors hover:text-destructive"
                title={`Remove ${ymd}`}
                aria-label={`Remove ${ymd}`}
              >
                <X className="size-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export function SlotFormSheet({
  open,
  onOpenChange,
  mode = "create",
  slot = null,
  selectedDate,
  defaultDoctorId = "",
  defaultTenantId = "",
  doctorOptions: doctorOptionsProp,
  tenantOptions: tenantOptionsProp,
  onSaved,
}) {
  const [form, setForm] = useState(() =>
    deriveInitialForm({ mode, slot, selectedDate, defaultDoctorId, defaultTenantId })
  );
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const [tenantOpts, setTenantOpts] = useState(() => doctorAndTenantOptionsArray(tenantOptionsProp));
  const [doctorOpts, setDoctorOpts] = useState(() => doctorAndTenantOptionsArray(doctorOptionsProp));
  const [calendarOpts, setCalendarOpts] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [doctorsLoading, setDoctorsLoading] = useState(false);
  const [calendarsLoading, setCalendarsLoading] = useState(false);
  const [canonicalSlotLoading, setCanonicalSlotLoading] = useState(false);
  const calendarsRequestRef = useRef(0);
  const canonicalSlotRequestRef = useRef(0);

  // Reset form whenever the sheet is (re)opened in a new mode/slot/date context.
  // For edit/duplicate mode, also fetch the canonical slot doc so recurrence/time
  // fields are accurate (the calendar API returns expanded occurrences without
  // the rule). Duplicate needs the same canonical hydration as edit because the
  // copy must start with the source's full recurrence shape.
  useEffect(() => {
    if (!open) return;
    setForm(deriveInitialForm({ mode, slot, selectedDate, defaultDoctorId, defaultTenantId }));
    setErrors({});
    setSubmitError("");

    if ((mode !== "edit" && mode !== "duplicate") || !slot) return;
    const slotId = String(slot?.mongoSlotId || slot?.id || "").trim();
    if (!slotId) return;

    const requestId = canonicalSlotRequestRef.current + 1;
    canonicalSlotRequestRef.current = requestId;
    setCanonicalSlotLoading(true);
    fetchAdminSlot({ slotId })
      .then((doc) => {
        if (canonicalSlotRequestRef.current !== requestId) return;
        if (!doc || typeof doc !== "object") return;
        setForm(
          deriveInitialForm({
            mode,
            slot,
            selectedDate,
            defaultDoctorId,
            defaultTenantId,
            canonicalDoc: doc,
          }),
        );
      })
      .catch(() => {
        // Keep the row-derived form; the user can still edit visible fields.
      })
      .finally(() => {
        if (canonicalSlotRequestRef.current === requestId) setCanonicalSlotLoading(false);
      });
  }, [open, mode, slot, selectedDate, defaultDoctorId, defaultTenantId]);

  // Lazy-load options if the parent didn't provide them.
  useEffect(() => {
    if (!open) return;
    if (Array.isArray(tenantOptionsProp) && tenantOptionsProp.length > 0) {
      setTenantOpts(doctorAndTenantOptionsArray(tenantOptionsProp));
      return;
    }
    setTenantsLoading(true);
    fetchTenants()
      .then((res) => {
        const items = Array.isArray(res?.items) ? res.items : [];
        setTenantOpts(items.map(toTenantOption).filter(Boolean));
      })
      .catch(() => setTenantOpts([]))
      .finally(() => setTenantsLoading(false));
  }, [open, tenantOptionsProp]);

  useEffect(() => {
    if (!open) return;
    if (Array.isArray(doctorOptionsProp) && doctorOptionsProp.length > 0) {
      setDoctorOpts(doctorAndTenantOptionsArray(doctorOptionsProp));
      return;
    }
    setDoctorsLoading(true);
    fetchDoctors()
      .then((res) => {
        const items = Array.isArray(res?.items) ? res.items : [];
        setDoctorOpts(items.map(toDoctorOption).filter(Boolean));
      })
      .catch(() => setDoctorOpts([]))
      .finally(() => setDoctorsLoading(false));
  }, [open, doctorOptionsProp]);

  // Calendar list depends on chosen doctor; refetch when it changes.
  useEffect(() => {
    if (!open) return;
    if (!form.doctorId) {
      setCalendarOpts([]);
      return;
    }
    const requestId = calendarsRequestRef.current + 1;
    calendarsRequestRef.current = requestId;
    setCalendarsLoading(true);
    fetchCalendars({ doctorId: form.doctorId })
      .then((res) => {
        if (calendarsRequestRef.current !== requestId) return;
        const items = Array.isArray(res?.items) ? res.items : [];
        const opts = items.map(toCalendarOption).filter(Boolean);
        setCalendarOpts(opts);
        setForm((prev) => {
          if (!prev.calendarId) return prev;
          const stillValid = opts.some((opt) => opt.value === prev.calendarId);
          return stillValid ? prev : { ...prev, calendarId: "" };
        });
      })
      .catch(() => {
        if (calendarsRequestRef.current !== requestId) return;
        setCalendarOpts([]);
      })
      .finally(() => {
        if (calendarsRequestRef.current === requestId) setCalendarsLoading(false);
      });
  }, [open, form.doctorId]);

  // When a calendar is selected, prefill timezone from it (only on create / when blank).
  useEffect(() => {
    if (!form.calendarId) return;
    const selectedCalendar = calendarOpts.find((opt) => opt.value === form.calendarId);
    const tz = String(selectedCalendar?.raw?.timezone || "").trim();
    if (tz) {
      setForm((prev) => (prev.timezone === tz ? prev : { ...prev, timezone: tz }));
    }
  }, [form.calendarId, calendarOpts]);

  const isRecurring = form.slotType === "recurring";
  const isMonthly = isRecurring && form.frequency === "monthly";
  const isMonthlyDayOfMonth = isMonthly && form.monthlyVariant === "dayOfMonth";
  const isMonthlyNthWeekday = isMonthly && form.monthlyVariant === "nthWeekday";
  const isDateList = isRecurring && form.frequency === "dates";

  const validate = () => {
    const next = {};
    if (!form.doctorId) next.doctorId = "Doctor is required.";
    if (!form.tenantId) next.tenantId = "Tenant is required.";
    if (!form.calendarId) next.calendarId = "Calendar is required.";
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.date)) next.date = "Pick a date.";
    if (!/^\d{2}:\d{2}$/.test(form.startTime)) next.startTime = "Set a start time.";
    if (!/^\d{2}:\d{2}$/.test(form.endTime)) next.endTime = "Set an end time.";

    const startDt = combineDateAndTimeInZone(form.date, form.startTime, form.timezone);
    const endDt = combineDateAndTimeInZone(form.date, form.endTime, form.timezone);
    if (startDt && endDt && endDt <= startDt) {
      next.endTime = "End must be after start.";
    }

    if (isRecurring && form.frequency === "weekly") {
      if (!Array.isArray(form.weekdays) || form.weekdays.length === 0) {
        next.weekdays = "Pick at least one weekday.";
      }
      const weeks = Number(form.repeatIntervalWeeks);
      if (!Number.isInteger(weeks) || weeks < 1) {
        next.repeatIntervalWeeks = "Repeat interval must be ≥ 1.";
      }
    }

    if (isMonthly) {
      const months = Number(form.repeatIntervalMonths);
      if (!Number.isInteger(months) || months < 1) {
        next.repeatIntervalMonths = "Repeat interval must be ≥ 1.";
      }
      if (isMonthlyDayOfMonth) {
        if (!Array.isArray(form.monthDays) || form.monthDays.length === 0) {
          next.monthDays = "Pick at least one day of the month.";
        }
      }
      if (isMonthlyNthWeekday) {
        if (!Array.isArray(form.monthWeeks) || form.monthWeeks.length === 0) {
          next.monthWeeks = "Pick at least one week of the month.";
        }
        if (!Array.isArray(form.weekdays) || form.weekdays.length === 0) {
          next.weekdays = "Pick at least one weekday.";
        }
      }
    }

    if (isDateList) {
      if (!Array.isArray(form.dates) || form.dates.length === 0) {
        next.dates = "Add at least one date.";
      } else if (!form.dates.every((d) => /^\d{4}-\d{2}-\d{2}$/.test(d))) {
        next.dates = "Each date must be a valid calendar date.";
      }
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const buildPayload = () => {
    const startDt = combineDateAndTimeInZone(form.date, form.startTime, form.timezone);
    const endDt = combineDateAndTimeInZone(form.date, form.endTime, form.timezone);

    const payload = {
      tenantId: form.tenantId,
      doctorId: form.doctorId,
      calendarId: form.calendarId,
      slotType: form.slotType,
      // Always send subType, even when blank, so editing an existing slot
      // can clear the field (server treats "" as "unset").
      subType: form.subType || "",
      bookingMode: form.bookingMode,
      time: {
        start: startDt.toUTC().toISO(),
        end: endDt.toUTC().toISO(),
        timezone: form.timezone || "UTC",
      },
    };

    const placeholder = String(form.placeholder || "").trim();
    if (placeholder) payload.placeholder = placeholder;

    // Compose metadata from the form's known fields. `_moreTests` is gated
    // on subType=followUp so changing the subtype away from followUp also
    // clears the flag on save.
    const metadata = {};
    const colorHex = String(form.colorHex || "").trim().toLowerCase();
    if (colorHex && /^#[0-9a-f]{6}$/.test(colorHex)) {
      metadata.colorHex = colorHex;
    }
    if (form.subType === "followUp" && form.moreTests === true) {
      metadata._moreTests = true;
    }
    if (Object.keys(metadata).length > 0) {
      payload.metadata = metadata;
    } else if (mode === "edit") {
      // Empty object on edit ensures the server clears any previously-set
      // colorHex / _moreTests that the form no longer carries.
      payload.metadata = {};
    }

    if (isRecurring) {
      const cleanedDates = Array.from(new Set(form.dates)).sort();
      const cleanedExcludeDates = Array.from(new Set(form.excludeDates)).sort();
      if (form.frequency === "monthly") {
        const recurrence = {
          frequency: "monthly",
          startTime: form.startTime,
          endTime: form.endTime,
          repeatIntervalMonths: Number(form.repeatIntervalMonths) || 1,
        };
        if (form.monthlyVariant === "dayOfMonth") {
          recurrence.monthDays = sortMonthDays(form.monthDays);
        } else {
          recurrence.monthWeeks = sortMonthWeeks(form.monthWeeks);
          recurrence.weekdays = [...form.weekdays].sort((a, b) => a - b);
        }
        if (cleanedDates.length > 0) recurrence.dates = cleanedDates;
        if (cleanedExcludeDates.length > 0) recurrence.excludeDates = cleanedExcludeDates;
        payload.recurrence = recurrence;
      } else if (form.frequency === "dates") {
        payload.recurrence = {
          frequency: "dates",
          startTime: form.startTime,
          endTime: form.endTime,
          dates: cleanedDates,
        };
      } else {
        const recurrence = {
          frequency: "weekly",
          weekdays: [...form.weekdays].sort((a, b) => a - b),
          startTime: form.startTime,
          endTime: form.endTime,
          repeatIntervalWeeks: Number(form.repeatIntervalWeeks) || 1,
        };
        if (cleanedDates.length > 0) recurrence.dates = cleanedDates;
        if (cleanedExcludeDates.length > 0) recurrence.excludeDates = cleanedExcludeDates;
        payload.recurrence = recurrence;
      }
    } else if (mode === "edit") {
      payload.recurrence = null;
    }

    return payload;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    if (!validate()) return;
    setSubmitError("");
    setSubmitting(true);
    try {
      const payload = buildPayload();
      if (mode === "edit") {
        const slotId = String(slot?.mongoSlotId || slot?.id || "").trim();
        if (!slotId) throw new Error("Missing slot id for update.");
        await updateAdminSlot({ slotId, body: payload });
        toast.success("Slot updated.");
      } else if (mode === "duplicate") {
        // Duplicate posts a brand-new slot — the source stays untouched.
        // `payload` already excludes any slot id (it's built from form state),
        // so this is a straight POST.
        await createAdminSlot({ body: payload });
        toast.success("Slot duplicated.");
      } else {
        await createAdminSlot({ body: payload });
        toast.success("Slot created.");
      }
      publishCalendarRefresh({ source: "slot-form" });
      if (typeof onSaved === "function") onSaved();
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to save slot.";
      setSubmitError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleWeekday = (iso) => {
    setForm((prev) => {
      const exists = prev.weekdays.includes(iso);
      const next = exists ? prev.weekdays.filter((n) => n !== iso) : [...prev.weekdays, iso];
      return { ...prev, weekdays: next };
    });
  };

  const toggleMonthDay = (value) => {
    setForm((prev) => {
      const exists = prev.monthDays.includes(value);
      const next = exists ? prev.monthDays.filter((n) => n !== value) : [...prev.monthDays, value];
      return { ...prev, monthDays: next };
    });
  };

  const toggleMonthWeek = (value) => {
    setForm((prev) => {
      const exists = prev.monthWeeks.includes(value);
      const next = exists ? prev.monthWeeks.filter((n) => n !== value) : [...prev.monthWeeks, value];
      return { ...prev, monthWeeks: next };
    });
  };

  const addDate = (date) => {
    const ymd = dateToYmd(date);
    if (!ymd) return;
    setForm((prev) => {
      if (prev.dates.includes(ymd)) return prev;
      return { ...prev, dates: [...prev.dates, ymd].sort() };
    });
  };

  const removeDate = (ymd) => {
    setForm((prev) => ({ ...prev, dates: prev.dates.filter((d) => d !== ymd) }));
  };

  const addExcludeDate = (date) => {
    const ymd = dateToYmd(date);
    if (!ymd) return;
    setForm((prev) => {
      if (prev.excludeDates.includes(ymd)) return prev;
      return { ...prev, excludeDates: [...prev.excludeDates, ymd].sort() };
    });
  };

  const removeExcludeDate = (ymd) => {
    setForm((prev) => ({ ...prev, excludeDates: prev.excludeDates.filter((d) => d !== ymd) }));
  };

  const dateValue = useMemo(() => ymdToDate(form.date), [form.date]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="h-full max-h-dvh w-full gap-0 p-0 sm:max-w-lg"
      >
        <SheetHeader className="relative z-10 shrink-0 border-b border-border bg-background px-6 pb-4 pl-6 pr-14 pt-5 text-left">
          <SheetTitle className="flex items-center gap-2">
            {mode === "edit"
              ? "Edit slot"
              : mode === "duplicate"
                ? "Duplicate slot"
                : "New slot"}
            {canonicalSlotLoading ? (
              <Loader2 className="size-4 animate-spin text-muted-foreground" aria-hidden />
            ) : null}
          </SheetTitle>
          <SheetDescription>
            {mode === "edit"
              ? "Update an existing slot. Recurring slots edit the entire pattern."
              : mode === "duplicate"
                ? "Pre-filled with the source slot's values — tweak anything you want changed, then save as a new slot definition."
                : "Create a slot for a doctor on a calendar. Pick static for a one-off, or recurring for a repeating pattern."}
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-full min-h-0 flex-1">
          <form onSubmit={handleSubmit} className="space-y-5 px-6 py-5 pb-32">
            <ComboboxField
              label="Doctor"
              required
              value={form.doctorId}
              options={doctorOpts}
              loading={doctorsLoading}
              onChange={(value) => setForm((prev) => ({ ...prev, doctorId: value }))}
              error={errors.doctorId}
              placeholder="Choose doctor"
            />
            <ComboboxField
              label="Clinic"
              required
              value={form.tenantId}
              options={tenantOpts}
              loading={tenantsLoading}
              onChange={(value) => setForm((prev) => ({ ...prev, tenantId: value }))}
              error={errors.tenantId}
              placeholder="Choose clinic"
            />
            <ComboboxField
              label="Calendar"
              required
              value={form.calendarId}
              options={calendarOpts}
              loading={calendarsLoading}
              disabled={!form.doctorId}
              onChange={(value) => setForm((prev) => ({ ...prev, calendarId: value }))}
              error={errors.calendarId}
              placeholder={form.doctorId ? "Choose calendar" : "Pick a doctor first"}
            />

            <Separator />

            <div className="space-y-2">
              <FieldLabel>Type</FieldLabel>
              <div className="flex gap-2">
                <PillButton
                  active={!isRecurring}
                  onClick={() => setForm((prev) => ({ ...prev, slotType: "static" }))}
                >
                  Static (one-off)
                </PillButton>
                <PillButton
                  active={isRecurring}
                  onClick={() => setForm((prev) => ({ ...prev, slotType: "recurring" }))}
                >
                  Recurring
                </PillButton>
              </div>
            </div>

            <div className="space-y-2">
              <FieldLabel>{isRecurring ? "Anchor date" : "Date"}</FieldLabel>
              <DatePicker
                date={dateValue}
                setDate={(next) => setForm((prev) => ({ ...prev, date: dateToYmd(next) }))}
              />
              {errors.date ? <p className="text-xs text-destructive">{errors.date}</p> : null}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <FieldLabel>Start</FieldLabel>
                <TimePicker
                  value={form.startTime}
                  onChange={(value) => setForm((prev) => ({ ...prev, startTime: value }))}
                  error={Boolean(errors.startTime)}
                />
                {errors.startTime ? <p className="text-xs text-destructive">{errors.startTime}</p> : null}
              </div>
              <div className="space-y-2">
                <FieldLabel>End</FieldLabel>
                <TimePicker
                  value={form.endTime}
                  onChange={(value) => setForm((prev) => ({ ...prev, endTime: value }))}
                  error={Boolean(errors.endTime)}
                />
                {errors.endTime ? <p className="text-xs text-destructive">{errors.endTime}</p> : null}
              </div>
            </div>

            <div className="space-y-2">
              <FieldLabel>Timezone</FieldLabel>
              <Input
                list="slot-form-tz-list"
                value={form.timezone}
                onChange={(e) => setForm((prev) => ({ ...prev, timezone: e.target.value.trim() }))}
                placeholder="UTC"
                className="h-9"
              />
              <datalist id="slot-form-tz-list">
                {COMMON_TIMEZONES.map((tz) => (
                  <option key={tz} value={tz} />
                ))}
              </datalist>
            </div>

            {isRecurring ? (
              <>
                <Separator />
                <div className="space-y-2">
                  <FieldLabel>Frequency</FieldLabel>
                  <div className="flex flex-wrap gap-2">
                    <PillButton
                      active={form.frequency === "weekly"}
                      onClick={() => setForm((prev) => ({ ...prev, frequency: "weekly" }))}
                    >
                      Weekly
                    </PillButton>
                    <PillButton
                      active={form.frequency === "monthly"}
                      onClick={() => setForm((prev) => ({ ...prev, frequency: "monthly" }))}
                    >
                      Monthly
                    </PillButton>
                    <PillButton
                      active={form.frequency === "dates"}
                      onClick={() => setForm((prev) => ({ ...prev, frequency: "dates" }))}
                      title="Repeat on an explicit list of calendar dates"
                    >
                      Specific dates
                    </PillButton>
                  </div>
                </div>

                {form.frequency === "dates" ? (
                  <div className="space-y-2">
                    <FieldLabel>Dates</FieldLabel>
                    <DateListPicker
                      dates={form.dates}
                      onAdd={addDate}
                      onRemove={removeDate}
                    />
                    {errors.dates ? <p className="text-xs text-destructive">{errors.dates}</p> : null}
                  </div>
                ) : form.frequency === "weekly" ? (
                  <>
                    <div className="space-y-2">
                      <FieldLabel>Weekdays</FieldLabel>
                      <div className="flex flex-wrap gap-1.5">
                        {WEEKDAY_LABELS.map((day) => (
                          <PillButton
                            key={day.iso}
                            active={form.weekdays.includes(day.iso)}
                            onClick={() => toggleWeekday(day.iso)}
                            title={`ISO ${day.iso}`}
                          >
                            {day.short}
                          </PillButton>
                        ))}
                      </div>
                      {errors.weekdays ? <p className="text-xs text-destructive">{errors.weekdays}</p> : null}
                    </div>
                    <div className="space-y-2">
                      <FieldLabel>Repeat every</FieldLabel>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min={1}
                          max={52}
                          value={form.repeatIntervalWeeks}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              repeatIntervalWeeks: Math.max(1, Number(e.target.value) || 1),
                            }))
                          }
                          className="h-9 w-24"
                        />
                        <span className="text-sm text-muted-foreground">week(s)</span>
                      </div>
                      {errors.repeatIntervalWeeks ? (
                        <p className="text-xs text-destructive">{errors.repeatIntervalWeeks}</p>
                      ) : null}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="space-y-2">
                      <FieldLabel>Monthly pattern</FieldLabel>
                      <div className="flex gap-2">
                        <PillButton
                          active={form.monthlyVariant === "dayOfMonth"}
                          onClick={() => setForm((prev) => ({ ...prev, monthlyVariant: "dayOfMonth" }))}
                          title="e.g. 1st and 15th of every month"
                        >
                          By day of month
                        </PillButton>
                        <PillButton
                          active={form.monthlyVariant === "nthWeekday"}
                          onClick={() => setForm((prev) => ({ ...prev, monthlyVariant: "nthWeekday" }))}
                          title="e.g. 2nd Tuesday of every month"
                        >
                          By Nth week
                        </PillButton>
                      </div>
                    </div>

                    {form.monthlyVariant === "dayOfMonth" ? (
                      <div className="space-y-2">
                        <FieldLabel>Days of the month</FieldLabel>
                        <div className="flex flex-wrap gap-1.5">
                          {MONTH_DAY_VALUES.map((day) => (
                            <PillButton
                              key={day}
                              active={form.monthDays.includes(day)}
                              onClick={() => toggleMonthDay(day)}
                              title={`Day ${day}`}
                            >
                              {day}
                            </PillButton>
                          ))}
                          <PillButton
                            active={form.monthDays.includes(-1)}
                            onClick={() => toggleMonthDay(-1)}
                            title="Last day of the month (28/29/30/31)"
                          >
                            Last
                          </PillButton>
                        </div>
                        {errors.monthDays ? <p className="text-xs text-destructive">{errors.monthDays}</p> : null}
                      </div>
                    ) : (
                      <>
                        <div className="space-y-2">
                          <FieldLabel>Week of the month</FieldLabel>
                          <div className="flex flex-wrap gap-1.5">
                            {MONTH_WEEK_LABELS.map((week) => (
                              <PillButton
                                key={week.value}
                                active={form.monthWeeks.includes(week.value)}
                                onClick={() => toggleMonthWeek(week.value)}
                                title={week.value === -1 ? "Last occurrence of that weekday in the month" : `${week.short} occurrence in the month`}
                              >
                                {week.short}
                              </PillButton>
                            ))}
                          </div>
                          {errors.monthWeeks ? <p className="text-xs text-destructive">{errors.monthWeeks}</p> : null}
                        </div>
                        <div className="space-y-2">
                          <FieldLabel>Weekdays</FieldLabel>
                          <div className="flex flex-wrap gap-1.5">
                            {WEEKDAY_LABELS.map((day) => (
                              <PillButton
                                key={day.iso}
                                active={form.weekdays.includes(day.iso)}
                                onClick={() => toggleWeekday(day.iso)}
                                title={`ISO ${day.iso}`}
                              >
                                {day.short}
                              </PillButton>
                            ))}
                          </div>
                          {errors.weekdays ? <p className="text-xs text-destructive">{errors.weekdays}</p> : null}
                        </div>
                      </>
                    )}

                    <div className="space-y-2">
                      <FieldLabel>Repeat every</FieldLabel>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min={1}
                          max={24}
                          value={form.repeatIntervalMonths}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              repeatIntervalMonths: Math.max(1, Number(e.target.value) || 1),
                            }))
                          }
                          className="h-9 w-24"
                        />
                        <span className="text-sm text-muted-foreground">month(s)</span>
                      </div>
                      {errors.repeatIntervalMonths ? (
                        <p className="text-xs text-destructive">{errors.repeatIntervalMonths}</p>
                      ) : null}
                    </div>
                  </>
                )}

                {form.frequency !== "dates" ? (
                  <div className="space-y-2">
                    <FieldLabel>Also include specific dates (optional)</FieldLabel>
                    <p className="text-xs text-muted-foreground/80">
                      Extra one-off dates added on top of the {form.frequency === "monthly" ? "monthly" : "weekly"} pattern. Duplicates that already fall on the pattern are ignored.
                    </p>
                    <DateListPicker
                      dates={form.dates}
                      onAdd={addDate}
                      onRemove={removeDate}
                    />
                    {errors.dates ? <p className="text-xs text-destructive">{errors.dates}</p> : null}
                  </div>
                ) : null}

                {form.frequency !== "dates" ? (
                  <div className="space-y-2">
                    <FieldLabel>Also exclude specific dates (optional)</FieldLabel>
                    <p className="text-xs text-muted-foreground/80">
                      One-off dates removed from the {form.frequency === "monthly" ? "monthly" : "weekly"} pattern. Overrides any matching included or pattern date.
                    </p>
                    <DateListPicker
                      dates={form.excludeDates}
                      onAdd={addExcludeDate}
                      onRemove={removeExcludeDate}
                    />
                  </div>
                ) : null}
              </>
            ) : null}

            <Separator />

            <div className="space-y-2">
              <FieldLabel>Visit subtype</FieldLabel>
              <div className="flex flex-wrap gap-2">
                {SUB_TYPE_OPTIONS.map((opt) => (
                  <PillButton
                    key={opt.value || "any"}
                    active={form.subType === opt.value}
                    onClick={() => setForm((prev) => ({ ...prev, subType: opt.value }))}
                    title={opt.value ? `Tag slot as ${opt.label.toLowerCase()}` : "No visit-type restriction"}
                  >
                    {opt.label}
                  </PillButton>
                ))}
              </div>
              {form.subType === "followUp" ? (
                <MoreTestsToggle
                  checked={form.moreTests === true}
                  onToggle={(next) => setForm((prev) => ({ ...prev, moreTests: next }))}
                />
              ) : null}
            </div>

            <div className="space-y-2">
              <FieldLabel>Booking mode</FieldLabel>
              <div className="flex gap-2">
                <PillButton
                  active={form.bookingMode === "auto"}
                  onClick={() => setForm((prev) => ({ ...prev, bookingMode: "auto" }))}
                  title="Patient self-serve"
                >
                  Auto
                </PillButton>
                <PillButton
                  active={form.bookingMode === "manual"}
                  onClick={() => setForm((prev) => ({ ...prev, bookingMode: "manual" }))}
                  title="Reception-only"
                >
                  Manual
                </PillButton>
              </div>
            </div>

            <div className="space-y-2">
              <FieldLabel>Label</FieldLabel>
              <Input
                value={form.placeholder}
                onChange={(e) => setForm((prev) => ({ ...prev, placeholder: e.target.value }))}
                placeholder="e.g. Morning clinic"
                className="h-9"
                maxLength={120}
              />
            </div>

            <div className="space-y-2">
              <FieldLabel>Color</FieldLabel>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.colorHex || "#0ea5e9"}
                  onChange={(e) => setForm((prev) => ({ ...prev, colorHex: e.target.value }))}
                  className="h-9 w-12 cursor-pointer rounded-md border border-border bg-transparent p-1"
                  aria-label="Slot color"
                />
                <Input
                  value={form.colorHex}
                  onChange={(e) => setForm((prev) => ({ ...prev, colorHex: e.target.value.trim() }))}
                  placeholder="#0ea5e9"
                  className="h-9 w-32 font-mono uppercase"
                  maxLength={7}
                />
                {form.colorHex ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-9 px-2 text-xs"
                    onClick={() => setForm((prev) => ({ ...prev, colorHex: "" }))}
                  >
                    Clear
                  </Button>
                ) : null}
              </div>
            </div>

            {submitError ? (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                {submitError}
              </div>
            ) : null}
          </form>
        </ScrollArea>

        <div className="shrink-0 border-t border-border bg-background px-6 py-4">
          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSubmit} disabled={submitting} aria-busy={submitting}>
              {submitting ? <Spinner className="mr-2 size-4" /> : null}
              {mode === "edit"
                ? "Save changes"
                : mode === "duplicate"
                  ? "Create duplicate"
                  : "Create slot"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// Sort month-day values so that the "Last day" sentinel (-1) lands at the end
// rather than the beginning, matching how the UI lists it.
function sortMonthDays(values) {
  return [...values].sort((a, b) => {
    if (a === -1) return 1;
    if (b === -1) return -1;
    return a - b;
  });
}

// Sort month-week values (1..4 with -1 = "Last"). Same idea as sortMonthDays.
function sortMonthWeeks(values) {
  return [...values].sort((a, b) => {
    if (a === -1) return 1;
    if (b === -1) return -1;
    return a - b;
  });
}

function doctorAndTenantOptionsArray(maybeArray) {
  if (!Array.isArray(maybeArray)) return [];
  return maybeArray
    .map((entry) => {
      if (!entry) return null;
      const value = String(entry.id || entry.value || "").trim();
      if (!value) return null;
      const label = String(entry.name || entry.label || value).trim();
      return { value, label, raw: entry };
    })
    .filter(Boolean);
}

function toCalendarOption(item) {
  const value = String(item?.id || item?._id || "").trim();
  if (!value) return null;
  // Rule hints ("· non-blocking · local only") ride the label so the picker explains itself.
  const label = calendarOptionLabel(item) || value;
  return { value, label, raw: item };
}
